import { useState, useEffect, useCallback, useRef } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  GlobalStyles,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  GridActionsCellItem,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  esES,
} from "@mui/x-data-grid";
import BatchPredictionIcon from "@mui/icons-material/BatchPrediction";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { PageHeaderCard } from "src/general_components/PageHeaderCard";
import {
  StyledDataGrid,
  estiloGrilla,
  globalEstilos,
} from "src/general_components/FunctionsListTablePaginated";
import {
  ocrLotesListar,
  ocrLotesCrear,
  ocrLotesRegistrarDoc,
  ocrLotesProcesar,
  ocrLotesEstado,
  ocrPlantillasListar,
} from "../helpers/OcrData";

// ── Constantes ────────────────────────────────────────────────────────────────

const BTN = {
  color: "#FFFFFF",
  fontFamily: "Roboto, sans-serif",
  background: "#0A395F",
  margin: "4px",
  minWidth: "120px",
  width: "120px",
  fontSize: "11px",
  height: "30px",
};

const ESTADO_CHIP = {
  pendiente: { label: "Pendiente", color: "default" },
  procesando: { label: "Procesando", color: "warning" },
  completado: { label: "Completado", color: "success" },
  con_errores: { label: "Con errores", color: "error" },
};

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

function formatFecha(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function EstadoChip({ estado, size = "small" }) {
  const cfg = ESTADO_CHIP[estado] ?? { label: estado, color: "default" };
  return <Chip label={cfg.label} color={cfg.color} size={size} />;
}

// ── Toolbar lista ─────────────────────────────────────────────────────────────

function CustomToolbar({ onNuevo }) {
  return (
    <GridToolbarContainer style={{ padding: 10, display: "flex", justifyContent: "space-between" }}>
      <Grid>
        <GridToolbarColumnsButton style={BTN} />
        <GridToolbarFilterButton style={BTN} />
        <GridToolbarDensitySelector style={BTN} />
        <GridToolbarExport style={BTN} />
      </Grid>
      <Button
        startIcon={<AddCircleIcon />}
        onClick={onNuevo}
        style={{ ...BTN, minWidth: 140, width: 140 }}
      >
        Nuevo lote
      </Button>
    </GridToolbarContainer>
  );
}

// ── Modal nuevo lote ──────────────────────────────────────────────────────────

function ModalNuevoLote({ open, onClose, onCreado }) {
  const [plantillas, setPlantillas] = useState([]);
  const [plantillaSeleccionada, setPlantillaSeleccionada] = useState(null);
  const [nombre, setNombre] = useState("");
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!open) return;
    ocrPlantillasListar().then(setPlantillas);
  }, [open]);

  const handleCrear = async () => {
    if (!plantillaSeleccionada) return;
    setGuardando(true);
    const res = await ocrLotesCrear({
      id_plantilla: plantillaSeleccionada.id,
      nombre: nombre.trim() || undefined,
    });
    setGuardando(false);
    if (res?.status === "true") {
      let lote = null;
      try { lote = JSON.parse(atob(res?.wsResponse)); } catch { /* noop */ }
      onCreado(lote);
    }
  };

  const handleClose = () => {
    setPlantillaSeleccionada(null);
    setNombre("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Nuevo lote</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Autocomplete
            options={plantillas}
            getOptionLabel={(o) => o.nombre ?? ""}
            value={plantillaSeleccionada}
            onChange={(_, v) => setPlantillaSeleccionada(v)}
            renderInput={(params) => (
              <TextField {...params} label="Plantilla OCR" required />
            )}
          />
          <TextField
            label="Nombre del lote (opcional)"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="El sistema asignará un nombre si se deja vacío"
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={guardando}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleCrear}
          disabled={!plantillaSeleccionada || guardando}
          startIcon={guardando ? <CircularProgress size={16} /> : undefined}
          style={{ background: "#0A395F", color: "#fff" }}
        >
          Crear
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Detalle de lote ───────────────────────────────────────────────────────────

function DetalleLote({ lote: loteInicial, onVolver }) {
  const [lote, setLote] = useState(loteInicial);
  const [archivos, setArchivos] = useState([]); // {file, nombre, estado, error}
  const [dragOver, setDragOver] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const pollingRef = useRef(null);
  const fileInputRef = useRef(null);

  // Polling mientras estado === 'procesando'
  const iniciarPolling = useCallback((id) => {
    if (pollingRef.current) return;
    pollingRef.current = setInterval(async () => {
      const estado = await ocrLotesEstado(id);
      if (estado) {
        setLote((prev) => ({ ...prev, ...estado }));
        if (estado.estado !== "procesando") {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      }
    }, 3000);
  }, []);

  useEffect(() => {
    if (lote?.estado === "procesando") {
      iniciarPolling(lote.id);
    }
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [lote?.estado, lote?.id, iniciarPolling]);

  const procesarArchivos = useCallback(async (files) => {
    const nuevos = Array.from(files).map((f) => ({
      file: f,
      nombre: f.name,
      estado: "pendiente",
      error: null,
    }));
    setArchivos((prev) => [...prev, ...nuevos]);

    for (let i = 0; i < nuevos.length; i++) {
      const item = nuevos[i];
      // Marcar como subiendo
      setArchivos((prev) =>
        prev.map((a) => (a.nombre === item.nombre && a.estado === "pendiente" ? { ...a, estado: "subiendo" } : a))
      );
      try {
        const b64 = await fileToBase64(item.file);
        const tipo = item.file.type || "application/octet-stream";
        const res = await ocrLotesRegistrarDoc({
          id_lote: lote.id,
          nombre_archivo: item.nombre,
          tipo_archivo: tipo,
          paginas: 1,
          archivo_b64: b64,
        });
        const ok = res?.status === "true";
        setArchivos((prev) =>
          prev.map((a) =>
            a.nombre === item.nombre && a.estado === "subiendo"
              ? { ...a, estado: ok ? "ok" : "error", error: ok ? null : (res?.message ?? "Error al registrar") }
              : a
          )
        );
      } catch (e) {
        setArchivos((prev) =>
          prev.map((a) =>
            a.nombre === item.nombre && a.estado === "subiendo"
              ? { ...a, estado: "error", error: String(e) }
              : a
          )
        );
      }
    }
  }, [lote.id]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) procesarArchivos(e.dataTransfer.files);
  };

  const handleInputChange = (e) => {
    if (e.target.files?.length) procesarArchivos(e.target.files);
    e.target.value = "";
  };

  const handleProcesar = async () => {
    setProcesando(true);
    await ocrLotesProcesar(lote.id);
    // Refrescar estado
    const estado = await ocrLotesEstado(lote.id);
    if (estado) {
      setLote((prev) => ({ ...prev, ...estado }));
      if (estado.estado === "procesando") iniciarPolling(lote.id);
    }
    setProcesando(false);
  };

  const puedeProcesar =
    archivos.length > 0 &&
    (lote.estado === "pendiente" || lote.estado === "con_errores") &&
    !procesando;

  const porcentaje = lote.porcentaje ?? (lote.total_docs > 0 ? Math.round((lote.procesados / lote.total_docs) * 100) : 0);
  const mostrarProgreso = ["procesando", "completado", "con_errores"].includes(lote.estado);
  const mostrarResultados = ["completado", "con_errores"].includes(lote.estado);
  const documentosResultado = lote.documentos ?? [];

  return (
    <Box>
      {/* Encabezado */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onVolver}
          style={{ ...BTN, width: "auto", minWidth: "auto" }}
        >
          Volver
        </Button>
        <Typography variant="h6" sx={{ flex: 1 }}>
          {lote.nombre || `Lote #${lote.id}`}
        </Typography>
        <EstadoChip estado={lote.estado} />
      </Stack>

      {/* Cargar documentos */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Cargar documentos
        </Typography>

        {/* Dropzone */}
        <Box
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            border: "2px dashed",
            borderColor: dragOver ? "primary.main" : "grey.400",
            borderRadius: 2,
            p: 4,
            textAlign: "center",
            cursor: "pointer",
            backgroundColor: dragOver ? "action.hover" : "background.default",
            transition: "background-color 0.2s, border-color 0.2s",
            mb: 2,
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 40, color: "text.secondary", mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Arrastrá archivos aquí o hacé clic para seleccionar
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Imágenes y PDF admitidos
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,.pdf"
            style={{ display: "none" }}
            onChange={handleInputChange}
          />
        </Box>

        {/* Lista de archivos */}
        {archivos.length > 0 && (
          <Stack spacing={1}>
            {archivos.map((a, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {a.estado === "subiendo" && <CircularProgress size={16} />}
                {a.estado === "ok" && <CheckCircleIcon color="success" fontSize="small" />}
                {a.estado === "error" && (
                  <Tooltip title={a.error ?? "Error"}>
                    <ErrorIcon color="error" fontSize="small" />
                  </Tooltip>
                )}
                {a.estado === "pendiente" && (
                  <Box sx={{ width: 16, height: 16, borderRadius: "50%", bgcolor: "grey.400" }} />
                )}
                <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                  {a.nombre}
                </Typography>
                <Chip
                  label={a.estado === "subiendo" ? "Subiendo…" : a.estado === "ok" ? "Listo" : a.estado === "error" ? "Error" : "Pendiente"}
                  size="small"
                  color={a.estado === "ok" ? "success" : a.estado === "error" ? "error" : "default"}
                />
                {a.estado === "subiendo" && (
                  <Box sx={{ width: 80 }}>
                    <LinearProgress />
                  </Box>
                )}
              </Box>
            ))}
          </Stack>
        )}

        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            startIcon={procesando ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
            disabled={!puedeProcesar}
            onClick={handleProcesar}
            style={{ background: "#0A395F", color: "#fff" }}
          >
            Procesar lote
          </Button>
        </Box>
      </Paper>

      {/* Progreso */}
      {mostrarProgreso && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Progreso
            </Typography>
            {lote.estado === "procesando" && <CircularProgress size={18} />}
          </Stack>
          <LinearProgress
            variant="determinate"
            value={porcentaje}
            sx={{ height: 10, borderRadius: 5, mb: 1 }}
            color={lote.estado === "con_errores" ? "error" : lote.estado === "completado" ? "success" : "primary"}
          />
          <Stack direction="row" spacing={3}>
            <Typography variant="body2">
              <strong>{porcentaje}%</strong> completado
            </Typography>
            <Typography variant="body2">
              {lote.procesados ?? 0} de {lote.total_docs ?? 0} documentos procesados
            </Typography>
            {(lote.con_errores ?? 0) > 0 && (
              <Typography variant="body2" color="error.main">
                {lote.con_errores} con errores
              </Typography>
            )}
          </Stack>
        </Paper>
      )}

      {/* Resultados */}
      {mostrarResultados && documentosResultado.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Resultados
          </Typography>
          <Stack spacing={1}>
            {documentosResultado.map((doc, i) => (
              <Box
                key={doc.id ?? i}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 2,
                  p: 1,
                  borderRadius: 1,
                  bgcolor: "background.default",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {doc.nombre_archivo ?? `Documento ${i + 1}`}
                  </Typography>
                  {doc.error_detalle && (
                    <Typography variant="caption" color="error.main">
                      {doc.error_detalle}
                    </Typography>
                  )}
                </Box>
                {doc.confianza_promedio != null && (
                  <Typography variant="body2" color="text.secondary">
                    Confianza: {Math.round(doc.confianza_promedio * 100) / 100}%
                  </Typography>
                )}
                <EstadoChip estado={doc.estado} />
              </Box>
            ))}
          </Stack>
        </Paper>
      )}
    </Box>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────

export function OcrLotes() {
  const theme = useTheme();
  const [lotes, setLotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loteActivo, setLoteActivo] = useState(null); // null = lista, objeto = detalle
  const [modalOpen, setModalOpen] = useState(false);

  const cargarLotes = useCallback(async () => {
    setLoading(true);
    const data = await ocrLotesListar();
    setLotes(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    cargarLotes();
  }, [cargarLotes]);

  const handleVerDetalle = (row) => setLoteActivo(row);

  const handleVolver = () => {
    setLoteActivo(null);
    cargarLotes();
  };

  const handleLoteCreado = (lote) => {
    setModalOpen(false);
    if (lote) {
      setLoteActivo(lote);
    } else {
      cargarLotes();
    }
  };

  const columns = [
    { field: "id", headerName: "ID", width: 70 },
    { field: "nombre", headerName: "Nombre", flex: 1 },
    {
      field: "plantilla",
      headerName: "Plantilla",
      flex: 1,
      valueGetter: (params) => params.row?.plantilla?.nombre ?? "—",
    },
    {
      field: "estado",
      headerName: "Estado",
      width: 120,
      renderCell: (params) => <EstadoChip estado={params.value} />,
    },
    { field: "total_docs", headerName: "Documentos", width: 90 },
    { field: "procesados", headerName: "Procesados", width: 90 },
    {
      field: "created_at",
      headerName: "Fecha",
      width: 130,
      renderCell: (params) => formatFecha(params.value),
    },
    {
      field: "acciones",
      headerName: "Acciones",
      type: "actions",
      width: 110,
      getActions: (params) => [
        <GridActionsCellItem
          icon={
            <Tooltip title="Ver detalle">
              <ArrowForwardIcon color="primary" />
            </Tooltip>
          }
          onClick={() => handleVerDetalle(params.row)}
          label="Ver detalle"
        />,
      ],
    },
  ];

  // Vista detalle
  if (loteActivo) {
    return (
      <Grid style={{ marginBottom: "50px", paddingTop: 10 }}>
        <PageHeaderCard
          title="Procesamiento por Lotes"
          icon={<BatchPredictionIcon />}
        />
        <Container maxWidth="xxl" style={{ padding: "16px 0px 50px 0px" }}>
          <DetalleLote lote={loteActivo} onVolver={handleVolver} />
        </Container>
      </Grid>
    );
  }

  // Vista lista
  return (
    <Grid style={{ marginBottom: "50px", paddingTop: 10 }}>
      <PageHeaderCard
        title="Procesamiento por Lotes"
        icon={<BatchPredictionIcon />}
      />

      <Container maxWidth="xxl" style={{ padding: "0px 0px 50px 0px" }}>
        <GlobalStyles styles={globalEstilos} />

        <StyledDataGrid
          rows={lotes}
          columns={columns}
          loading={loading}
          density="standard"
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          disableRowSelectionOnClick
          autoHeight={lotes.length > 0}
          components={{
            Toolbar: () => <CustomToolbar onNuevo={() => setModalOpen(true)} />,
          }}
          sx={{
            ...estiloGrilla,
            backgroundColor: theme.palette.background.paper,
          }}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          pageSizeOptions={[25, 50, 100]}
          pagination
        />
      </Container>

      <ModalNuevoLote
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreado={handleLoteCreado}
      />
    </Grid>
  );
}
